import React from "react"
import PropTypes from "prop-types"
import Img from "gatsby-image/withIEPolyfill"
import styled from "styled-components"
import { up } from "styled-breakpoints"
import Observer from "react-intersection-observer"
import { gsap } from "gsap/all"
import { isBrowser } from "react-device-detect"

import { ContainerContext } from "../../global/container"
import HorizontalScrollIndicator from "../../atomic/organisms/HorizontalScrollIndicator"
import { H1 } from "../../atomic/atoms/type/H1"
import { H3 } from "../../atomic/atoms/type/H3"
import { PRE } from "../../atomic/atoms/type/PRE"
import { TYPE } from "../../../theme/styles/globals"

export default class ContentfulModuleHeader extends React.Component {

  componentDidMount(){

    const {
      contentful_id,
    } = this.props

    document.getElementById(`intro-video-${contentful_id}`).muted = true
    document.getElementById(`intro-video-${contentful_id}`).volume = 0
  }

  render() {

    const {
      image,
      backgroundVideo,
      mobileVideo,
      video,
      title,
      subTitle,
      services,
      contentful_id,
    } = this.props

    let vw = 0

    if (typeof window !== "undefined") {
      vw =
        (window.innerWidth ||
          document.documentElement.clienWidth ||
          document.body.clientWidth) / 3
      vw = vw * -1
    }

    return (
      <Wrapper>
        <InView
          tag="div"
          onChange={inView => {
            if (!backgroundVideo) return
            inView
              ? document.getElementById(`intro-video-${contentful_id}`).play()
              : document.getElementById(`intro-video-${contentful_id}`).pause()
          }}
          rootMargin={`0px ${vw}px`}
        >
          <HorizontalScrollIndicator />
          <Text>
            <div>
              <Title color="white">{title}</Title>
              {services && <Services color="white">{services}</Services>}
              {subTitle && <SubTitle color="white">{subTitle}</SubTitle>}
              {video && (
                <ContainerContext.Consumer>
                  {({ playVideo }) => (
                    <PlayButton onClick={() => isBrowser ? playVideo(video.file.url) : window.open(video.file.url, "_blank") }>
                      <PlayLabel>Enjoy the show</PlayLabel>
                      <PlaySVG 
                        viewBox="0 0 72 72"
                        onMouseEnter={ () => gsap.to("#play-circle", { rotation:180, ease:"sine.easeOut", duration:.25, transformOrigin:"50% 50%" }) }
                        onMouseLeave={ () => gsap.to("#play-circle", { rotation:0, ease:"sine.easeOut", duration:.25 }) }
                      >
                        <path
                          d="M36.2 1C16.8 1 1 16.8 1 36.2s15.8 35.2 35.2 35.2 35.2-15.8 35.2-35.2c0-6-1.5-11.7-4.2-16.6-2.9-5.5-7.3-10.1-12.5-13.3"
                          fill="none"
                          stroke="#fff"
                          strokeWidth={1.1}
                          id="play-circle"
                        />
                        <path fill="#fff" d="M50.6 36.2l-22 11v-22z" />
                      </PlaySVG>
                    </PlayButton>
                  )}
                </ContainerContext.Consumer>
              )}
            </div>
          </Text>
          {mobileVideo && (
            <MobileVideoWrap>
              <MobileVideo
                autoPlay
                loop
                muted
                playsInline
                id={`mobile-video-${contentful_id}`}
              >
                <source src={mobileVideo.file.url} type="video/mp4" />
              </MobileVideo>
            </MobileVideoWrap>
          )}
          {backgroundVideo && (
            <VideoWrap>
              <Video
                autoPlay
                loop
                muted
                playsInline
                id={`intro-video-${contentful_id}`}
              >
                <source src={backgroundVideo.file.url} type="video/mp4" />
              </Video>
            </VideoWrap>
          )}
          <Image fluid={image.fluid} />
        </InView>
      </Wrapper>
    )

  }

}

ContentfulModuleHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  video: PropTypes.object,
  image: PropTypes.object.isRequired,
}

const Wrapper = styled.section`
  width: 100%;
  height: 90vh;
  background-color: black;
  position: relative;

  ${up("md")} {
    width: 100vw;
    height: 100vh;
  }
`

const InView = styled(Observer)`
  width: 100%;
  height: 100%;
  position: relative;
`

const Text = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 3;
  align-items: flex-end;
  padding-top: 10%;
  padding-bottom: 15%;
  padding-left: 32px;
  padding-right: 32px;

  ${up("md")} {
    padding-top: 10%;
    padding-bottom: 10%;
    padding-left: 60px;
    padding-right: 60px;
    width: 75%;
  }

  ${up("lg")} {
    padding-top: 10%;
    padding-bottom: 10%;
    padding-left: 60px;
    padding-right: 60px;
    width: 75%;
  }

  ${up("xl")} {
    padding-top: 10%;
    padding-bottom: 5%;
    padding-left: 60px;
    padding-right: 60px;
  }

  ${up("xxl")} {
    width: 70%;
  }
`

const Video = styled.video`
  display: none;

  ${up("md")} {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.2;
  }
`

const VideoWrap = styled.div`
  display: none;

  ${up("md")} {
    display: block;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    background-color: black;
    z-index: 2;
    position: absolute;
  }

  @media (min-aspect-ratio: 16/9) {
    ${Video} {
      height: 300%;
      top: -100%;
    }
  }

  @media (max-aspect-ratio: 16/9) {
    ${Video} {
      width: 300%;
      left: -100%;
    }
  }
`

const Image = styled(Img)`
  width: 100%;
  height: 100%;
  display: block;
  position: absolute !important;
  top: 0px;
  left: 0px;
  z-index: 1;
  opacity: 0.8;
`

const Title = styled(H1)`
  font-size: 46px;
  line-height: 46px;
  margin-bottom:44px;

  ${up("md")}{
    font-size:10vh;
    line-height:10vh;
    margin-bottom:4vh;
  }

`
const Services = styled(PRE)`
  margin-bottom: 30px;
  display: block;
  line-height: 18px;
  ${up("md")} {
    margin-left: 10px;
  }
`
const SubTitle = styled(H3)`
  margin-bottom: 30px;
  font-size: 22px;
  line-height: 26px;
  font-family:${TYPE.SERIF_FONT};

  ${up("md")} {
    margin-left: 9px;
    line-height: 40px;
    font-size: 3vh;
    line-height: 4vh;
  }

  ${up("lg")} {
    /* width:70%; */
  }
`

const PlayButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`

const PlayLabel = styled(PRE)`
  color: white;
`
const PlaySVG = styled.svg`
  width: 70px;
  height: 70px;
  margin-left: 20px;
`

const MobileVideoWrap = styled.div`
  position:absolute;
  top:0;
  z-index:2;
  background:black;
  height:100%;
  width:100%;
  overflow:hidden;

  ${up("md")} { 
    display:none;
  }
`

const MobileVideo = styled.video`
  width:110%;
  height:100%;
  opacity:.4;
  position:absolute;
  top:0;
  left:-5%;

  ${up("md")} {
    display:none;
  }
`